import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight, X } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, MaskReveal } from "@/components/Reveal";
import { Reviews } from "@/components/Reviews";
import { BeforeAfter } from "@/components/BeforeAfter";
import { Portfolio } from "@/components/Portfolio";
import { Team } from "@/components/Team";
import { LocationMap } from "@/components/LocationMap";
import { SocialProof } from "@/components/SocialProof";
import { Countdown } from "@/components/Countdown";
import { PRICING, PACKAGES, IMAGES, INFO } from "@/data/site";
import { usePublicHours } from "@/data/usePublicHours";
import { useLang } from "@/i18n";

const Hero = () => {
  const ref = useRef(null);
  const { t } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-screen flex items-end overflow-hidden pt-20">
      <div className="mx-auto max-w-[1400px] w-full px-5 md:px-10 pb-16 md:pb-24 grid md:grid-cols-12 gap-10 items-end relative z-10">
        <div className="md:col-span-7">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="w-10 h-px bg-primary" />
            <span className="text-xs uppercase tracking-[0.25em] text-primary">{t("hero.tagline")}</span>
          </motion.div>

          <h1 className="font-serif font-light leading-[0.92] tracking-tighter text-6xl md:text-8xl lg:text-9xl">
            <MaskReveal lines={["Maschas", "Atelier"]} delay={0.15} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7 }}
            className="mt-8 max-w-md text-base md:text-lg text-foreground/70 leading-relaxed"
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
          >
            <Countdown />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85 }}
          className="md:col-span-5 flex md:justify-end"
        >
          <div className="flex flex-col sm:flex-row md:flex-col gap-3 w-full sm:w-auto">
            <Link to="/buchung" data-testid="hero-book-btn"
              className="group flex items-center justify-between gap-6 bg-primary text-primary-foreground px-7 py-4 min-w-[200px]">
              <span className="text-sm tracking-wide">{t("hero.cta_book")}</span>
              <ArrowRight strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a href="#preise" data-testid="hero-prices-btn"
              className="group flex items-center justify-between gap-6 border border-foreground px-7 py-4 min-w-[200px]">
              <span className="text-sm tracking-wide">{t("hero.cta_services")}</span>
              <ArrowRight strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <Link to="/schulungen" data-testid="hero-academy-btn"
              className="group flex items-center justify-between gap-6 border border-primary/60 bg-primary/10 px-7 py-4 min-w-[200px] text-primary transition-colors hover:bg-primary hover:text-primary-foreground">
              <span className="text-sm tracking-wide">{t("hero.cta_academy")}</span>
              <ArrowRight strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </motion.div>
      </div>

      <motion.div style={{ y, scale }} className="absolute top-0 right-0 h-full w-full md:w-[42%] -z-0">
        <div className="relative h-full w-full overflow-hidden">
          <img src={IMAGES.hero} alt="Mascha – Maschas Atelier" className="h-full w-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/10 to-transparent md:from-background/60" />
        </div>
      </motion.div>
    </section>
  );
};

const chapters = [
  { n: "01", key: "ch1" },
  { n: "02", key: "ch2" },
  { n: "03", key: "ch3" },
];

const About = () => {
  const { t } = useLang();
  return (
    <section data-testid="about" className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
      <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-center">
        <div className="md:col-span-5">
          <Reveal>
            <div className="overflow-hidden">
              <img src={IMAGES.about} alt="Über Maschas Atelier" className="w-full aspect-[4/5] object-cover" />
            </div>
          </Reveal>
        </div>
        <div className="md:col-span-7">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{t("about.eyebrow")}</span>
            <h2 className="font-serif font-light text-4xl md:text-6xl tracking-tight mt-6 leading-[1.05]">
              {t("about.title")}
            </h2>
            <p className="mt-8 text-foreground/70 leading-relaxed max-w-xl">
              {t("about.body")}
            </p>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-3 gap-8 border-t border-border pt-10">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.1}>
                <div className="font-serif text-3xl text-primary mb-3">{c.n}</div>
                <h3 className="text-lg mb-2">{t(`about.${c.key}_title`)}</h3>
                <p className="text-sm text-foreground/60 leading-relaxed">{t(`about.${c.key}_text`)}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const ServicesBand = () => {
  const { t } = useLang();
  return (
    <section data-testid="services" className="bg-secondary">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32 grid md:grid-cols-2 gap-6">
        <Reveal className="md:row-span-2">
          <a href="#preise-manikure" data-testid="svc-card-manikure" className="group relative h-full min-h-[420px] overflow-hidden block cursor-pointer">
            <img src={IMAGES.manikure} alt={t("svc.manikure_title")} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-foreground/30 group-hover:bg-foreground/40 transition-colors duration-500" />
            <div className="relative z-10 p-8 md:p-10 h-full flex flex-col justify-end text-background">
              <span className="text-xs uppercase tracking-[0.25em] opacity-80">{t("svc.from")} 45 €</span>
              <h3 className="font-serif text-4xl md:text-5xl font-light mt-3">{t("svc.manikure_title")}</h3>
            </div>
          </a>
        </Reveal>
        <Reveal delay={0.1}>
          <a href="#preise-pedikure" data-testid="svc-card-pedikure" className="group relative min-h-[200px] h-full overflow-hidden block cursor-pointer">
            <img src={IMAGES.pedicureCard} alt={t("svc.pedikure_title")} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-foreground/30 group-hover:bg-foreground/40 transition-colors duration-500" />
            <div className="relative z-10 p-8 h-full flex flex-col justify-end text-background">
              <span className="text-xs uppercase tracking-[0.25em] opacity-80">{t("svc.from")} 55 €</span>
              <h3 className="font-serif text-3xl md:text-4xl font-light mt-3">{t("svc.pedikure_title")}</h3>
            </div>
          </a>
        </Reveal>
        <Reveal delay={0.2}>
          <Link to="/schulungen" className="group relative min-h-[200px] h-full overflow-hidden block">
            <img src={IMAGES.schulungenCard} alt={t("svc.schulungen_title")} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-primary/70 group-hover:bg-primary/80 transition-colors duration-500" />
            <div className="relative z-10 p-8 h-full flex flex-col justify-between text-primary-foreground">
              <ArrowUpRight strokeWidth={1.25} className="self-end transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              <div>
                <span className="text-xs uppercase tracking-[0.25em] opacity-80">{t("svc.soon")}</span>
                <h3 className="font-serif text-3xl md:text-4xl font-light mt-3">{t("svc.schulungen_title")}</h3>
              </div>
            </div>
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

const PriceList = () => {
  const { t } = useLang();
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox]);

  return (
    <section id="preise" data-testid="price-list" className="mx-auto max-w-[1100px] px-5 md:px-10 py-24 md:py-32">
      <Reveal>
        <div className="flex items-end justify-between flex-wrap gap-4 mb-16">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{t("prices.eyebrow")}</span>
            <h2 className="font-serif font-light text-4xl md:text-6xl tracking-tight mt-4">{t("prices.title")}</h2>
          </div>
          <Link to="/buchung" className="group flex items-center gap-2 text-sm text-primary">
            {t("prices.cta")}
            <ArrowRight strokeWidth={1.5} size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </Reveal>

      {/* PACKAGES — prominently on top */}
      <Reveal>
        <div className="mb-20" data-testid="packages-section">
          <div className="flex items-baseline gap-4 mb-3">
            <span className="text-xs uppercase tracking-[0.25em] text-primary">{t("packages.eyebrow")}</span>
            <span className="flex-1 border-b border-primary/30" />
          </div>
          <h3 className="font-serif text-3xl md:text-4xl font-light mb-3">{t("packages.title")}</h3>
          <p className="text-sm text-muted-foreground mb-8 max-w-2xl">{t("packages.subtitle")}</p>

          <div className="grid md:grid-cols-2 gap-5">
            {PACKAGES.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06}>
                <article data-testid={`package-${p.id}`} className={`relative h-full flex flex-col p-8 md:p-9 border ${p.featured ? "border-primary bg-primary/5" : "border-border bg-background"}`}>
                  {p.featured && (
                    <span className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.25em] bg-primary text-primary-foreground px-2.5 py-1">
                      {t("packages.featured")}
                    </span>
                  )}
                  <div className="flex items-start justify-between gap-6 mb-2">
                    <div>
                      <h4 className="font-serif text-3xl md:text-4xl font-light leading-tight">{t(p.nameKey)}</h4>
                      <p className="italic text-primary/80 text-sm mt-2">{t(p.tagKey)}</p>
                    </div>
                    <span className="font-serif text-3xl text-primary whitespace-nowrap">{p.price}</span>
                  </div>
                  <p className="text-sm text-foreground/70 leading-relaxed mt-4">
                    {t(p.bodyKey)}
                  </p>
                  <div className="mt-6 pt-5 border-t border-border">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{t("packages.includes")}</span>
                    <p className="text-sm text-foreground/85 mt-2 leading-relaxed">{t(p.includesKey)}</p>
                  </div>
                  <Link
                    to={`/buchung?pkg=${p.id}`}
                    data-testid={`package-book-${p.id}`}
                    className={`group mt-6 inline-flex items-center justify-between gap-3 px-5 py-3 text-sm tracking-wide transition-colors ${
                      p.featured ? "bg-primary text-primary-foreground hover:opacity-90" : "border border-foreground/30 hover:border-foreground"
                    }`}
                  >
                    <span>{t("packages.book")}</span>
                    <ArrowRight strokeWidth={1.5} size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </Reveal>

      <div className="space-y-16">
        {PRICING.map((cat, ci) => (
          <Reveal key={cat.group} delay={ci * 0.05}>
            <h3 id={cat.anchor} className="font-serif text-2xl md:text-3xl mb-2 scroll-mt-24">{cat.groupKey ? t(cat.groupKey) : cat.group}</h3>
            {(cat.noteKey || cat.note) && <p className="text-sm text-muted-foreground mb-6">{cat.noteKey ? t(cat.noteKey) : cat.note}</p>}
            <ul className="divide-y divide-border border-t border-border">
              {cat.items.map((it, ii) => (
                <li key={it.name} className="flex items-center justify-between gap-4 md:gap-6 py-4">
                  {it.photo && (
                    <button
                      type="button"
                      data-testid={`price-photo-${ci}-${ii}`}
                      onClick={() => setLightbox({ src: it.photo, alt: it.key ? t(it.key) : it.name })}
                      aria-label={`Foto vergrößern: ${it.key ? t(it.key) : it.name}`}
                      className="group/photo shrink-0 cursor-zoom-in overflow-hidden border border-border/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                    >
                      <img
                        src={it.photo}
                        alt={it.key ? t(it.key) : it.name}
                        loading="lazy"
                        className="w-24 h-32 md:w-[120px] md:h-[160px] object-cover transition-transform duration-500 group-hover/photo:scale-105"
                      />
                    </button>
                  )}
                  <div className="max-w-md flex-1">
                    <span className="text-foreground/85">{it.key ? t(it.key) : it.name}</span>
                    {(it.descKey || it.desc) && <p className="text-sm text-foreground/45 mt-1 leading-relaxed">{it.descKey ? t(it.descKey) : it.desc}</p>}
                  </div>
                  <span className="flex-1 border-b border-dotted border-border/70 mx-2 hidden sm:block" />
                  <span className="font-serif text-xl whitespace-nowrap">{it.price}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        ))}
      </div>

      {lightbox && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25 }}
          data-testid="price-lightbox"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 md:p-10"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            data-testid="price-lightbox-close"
            onClick={() => setLightbox(null)}
            aria-label="Schließen"
            className="absolute top-5 right-5 md:top-8 md:right-8 text-white/70 hover:text-white transition-colors"
          >
            <X strokeWidth={1.25} size={34} />
          </button>
          <motion.img
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            src={lightbox.src}
            alt={lightbox.alt}
            data-testid="price-lightbox-image"
            className="max-h-[85vh] max-w-[92vw] object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <span
            data-testid="price-lightbox-caption"
            className="absolute bottom-5 md:bottom-8 left-1/2 -translate-x-1/2 text-white/70 text-sm tracking-wide text-center px-4"
          >
            {lightbox.alt}
          </span>
        </motion.div>
      )}
    </section>
  );
};

const FAQ_COUNT = 8;

const Faq = () => {
  const { t } = useLang();
  return (
    <section id="faq" data-testid="faq-section" className="mx-auto max-w-[1400px] px-5 md:px-10 pb-24 md:pb-32">
      <div className="grid md:grid-cols-12 gap-10 md:gap-16">
        <Reveal className="md:col-span-4">
          <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{t("faq.eyebrow")}</span>
          <h2 className="font-serif font-light text-4xl md:text-5xl tracking-tight mt-4">{t("faq.title")}</h2>
          <p className="text-sm text-foreground/60 leading-relaxed mt-6 max-w-xs">
            {t("faq.subtitle")}{" "}
            <Link to="/kontakt" data-testid="faq-contact-link" className="text-primary underline-offset-4 hover:underline">
              {t("faq.subtitle_cta")}
            </Link>
          </p>
        </Reveal>
        <Reveal delay={0.1} className="md:col-span-8">
          <Accordion type="single" collapsible className="border-t border-border">
            {Array.from({ length: FAQ_COUNT }, (_, i) => i + 1).map((n) => (
              <AccordionItem key={n} value={`faq-${n}`} data-testid={`faq-item-${n}`} className="border-b border-border">
                <AccordionTrigger data-testid={`faq-trigger-${n}`} className="group py-6 hover:no-underline">
                  <span className="pr-6 text-left font-serif text-xl md:text-2xl font-light transition-colors group-hover:text-primary">
                    {t(`faq.q${n}`)}
                  </span>
                </AccordionTrigger>
                <AccordionContent data-testid={`faq-content-${n}`} className="max-w-2xl pb-6 text-base text-foreground/70 leading-relaxed">
                  {t(`faq.a${n}`)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
};

const HoursCTA = () => {
  const { t } = useLang();
  const HOURS = usePublicHours();
  return (
    <section data-testid="hours-cta" className="mx-auto max-w-[1400px] px-5 md:px-10 pb-24 md:pb-32">
      <div className="grid md:grid-cols-2 gap-6">
        <Reveal>
          <div className="bg-muted p-10 md:p-14 h-full">
            <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{t("hours.eyebrow")}</span>
            <ul className="mt-8 space-y-3">
              {HOURS.map((h) => (
                <li key={h.day} className="flex justify-between border-b border-border pb-3 text-foreground/80">
                  <span>{h.dayKey ? t(h.dayKey) : h.day}</span>
                  <span>{h.closedKey ? t(h.closedKey) : h.time}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{t("hours.payment_label")}</span>
              <p className="mt-3 text-sm text-foreground/70 leading-relaxed">
                {t("hours.payment_body")}<strong className="font-medium text-foreground/90">{t("hours.payment_body_strong")}</strong>.
              </p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="bg-primary text-primary-foreground p-10 md:p-14 h-full flex flex-col justify-between">
            <div>
              <span className="text-xs uppercase tracking-[0.25em] opacity-70">{t("hours.cta_eyebrow")}</span>
              <h2 className="font-serif font-light text-4xl md:text-5xl mt-6 leading-tight">
                {t("hours.cta_title")}
              </h2>
              <p className="mt-6 text-primary-foreground/75 max-w-sm">
                {t("hours.cta_body")}
              </p>
            </div>
            <Link to="/buchung" data-testid="cta-book-btn"
              className="group mt-10 inline-flex items-center justify-between gap-6 bg-primary-foreground text-primary px-7 py-4 w-full sm:w-auto">
              <span className="text-sm tracking-wide">{t("hours.cta_button")}</span>
              <ArrowRight strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <div className="bg-secondary text-foreground py-5 marquee-fade border-y border-border">
        <Marquee speed={40} gradient={false}>
          {Array(6).fill(0).map((_, i) => (
            <span key={i} className="font-serif text-2xl md:text-3xl font-light mx-8 flex items-center gap-8">
              Maschas Atelier <span className="text-primary">·</span> Studio &amp; Academy <span className="text-primary">·</span>
            </span>
          ))}
        </Marquee>
      </div>
      <About />
      <ServicesBand />
      <BeforeAfter />
      <Portfolio />
      <PriceList />
      <Faq />
      <Team />
      <Reviews />
      <SocialProof />
      <LocationMap />
      <HoursCTA />
    </div>
  );
}
